import { type FC, useState } from "react"
import styled from "styled-components"

import routes from "@forento/shared/routes"

import { useAlert } from "~/contexts/AlertContext"
import { usePlatform } from "~/contexts/PlatformContext"
import { useUser } from "~/contexts/UserContext"
import Button, { SubmitButton } from "~/themes/school/components/Button"
import { CheckmarkIcon, CloseIcon } from "~/themes/school/components/Icon"
import InputField from "~/themes/school/components/InputField"
import Layout from "~/themes/school/components/Layout"
import { PageTitle } from "~/themes/school/components/PageHeader"
import { query } from "~/utilities/trpc"

import Group from "./Group"
import Notifications from "./Notifications"
import ProfileAvatar from "./ProfileAvatar"

const SettingsPage: FC = () => {
	const platform = usePlatform().platform
	const user = useUser()
	const alert = useAlert()

	const [firstName, setFirstName] = useState(user.user!.firstName)
	const [lastName, setLastName] = useState(user.user!.lastName)
	const [currentPassword, setCurrentPassword] = useState("")
	const [newPassword, setNewPassword] = useState("")
	const [newPasswordConfirmation, setNewPasswordConfirmation] = useState("")
	const [deleteAccountPassword, setDeleteAccountPassword] = useState("")

	const personalInfo = query.user.update.useMutation({
		async onSuccess() {
			await user.reload()
			await alert.show("Success", "Personal info saved successfully!")
		},
		async onError(error) {
			console.error(error)
			await alert.show("Error", "Failed to save personal info. Please try again later.")
		},
	})
	const verifyBokaderoMember = query.user.verifyBokaderoMember.useMutation({
		async onSuccess(isMember) {
			await user.reload()
			if (isMember) {
				await alert.show("Tack!", "Ditt medlemskap i Bokadero Arena har nu verifierats!")
			} else {
				await alert.show(
					"Inget medlemskap",
					"Det finns inget medlemskap i Bokadero Arena kopplat till din e-postadress. Om du är medlem men ändå inte kan verifiera ditt medlemskap, kontakta [hej@bokadero.se](mailto:hej@bokadero.se).",
				)
			}
		},
		async onError(error) {
			console.error(error)
			await alert.show("Error", "Kunde inte verifiera medlemskap i Bokadero Arena. Vänligen försök igen senare.")
		},
	})
	const changePassword = query.user.setPassword.useMutation({
		async onSuccess({ status }) {
			if (status === "invalid-current") {
				await alert.show("Error", "Incorrect current password.")
				return
			}

			await user.reload()

			setCurrentPassword("")
			setNewPassword("")
			setNewPasswordConfirmation("")

			await alert.show("Success", "Password changed successfully!")
		},
		async onError(error) {
			console.error(error)
			await alert.show("Error", "Failed to change password. Please try again later.")
		},
	})
	const deleteAccount = query.user.delete.useMutation({
		async onSuccess(response) {
			if (response.status === "invalid-password") {
				await alert.show("Error", "Invalid password.")
				return
			}
			await user.reload()
			await alert.show("Success", "Account deleted successfully!")
		},
		async onError(error) {
			console.error(error)
			await alert.show("Error", "Failed to delete account. Please try again later.")
		},
	})

	return (
		<Layout>
			<PageTitle>Settings</PageTitle>

			<Groups>
				<Group
					title="Personal information"
					onSubmit={() => {
						if (firstName.trim() === "" || lastName.trim() === "") {
							alert.show("Error", "Please fill in all fields.")
							return
						}
						personalInfo.mutate({ firstName, lastName })
					}}
					fullWidth
				>
					<InputField label="Email address" value={user.user!.email} disabled />
					<Row>
						<InputField
							label="First name"
							value={firstName}
							onChange={setFirstName}
							autoComplete="given-name"
						/>
						<InputField
							label="Last name"
							value={lastName}
							onChange={setLastName}
							autoComplete="family-name"
						/>
						<SubmitButton variant="primary" isLoading={personalInfo.isPending}>
							Save changes
						</SubmitButton>
					</Row>
				</Group>

				<Group title="Notifications">
					{user.user!.isCreator ? (
						<p>
							Manage your notifications in{" "}
							<Button
								variant="link"
								onClick={`https://app.forento.io${routes.app.settings.notification()}`}
							>
								admin settings
							</Button>
							.
						</p>
					) : (
						<Notifications />
					)}
				</Group>

				{user.user!.communityProfileId !== null && (
					<Group title="Community avatar">
						<ProfileAvatar />
					</Group>
				)}

				{platform.specialPlatform === "bokadero" && (
					<Group title="Medlemskap i Bokadero Arena" onSubmit={verifyBokaderoMember.mutate}>
						{user.user?.isBokaderoMember ? (
							<div className="flex items-center gap-4 rounded-lg border border-green-200 bg-green-50 p-4">
								<div className="flex-shrink-0">
									<CheckmarkIcon className="size-16 text-success" />
								</div>
								<div className="flex-1">
									<h3 className="mb-1 font-semibold text-green-800">Medlem verifierad</h3>
									<p className="text-green-700">Du är medlem i Bokadero Arena</p>
								</div>
							</div>
						) : (
							<div className="flex grow flex-col gap-4">
								<div className="flex items-start gap-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
									<div className="mt-1 flex-shrink-0">
										<CloseIcon className="size-16 text-amber-600" />
									</div>
									<div className="flex-1">
										<h3 className="mb-2 font-semibold text-amber-800">Medlemskap ej verifierat</h3>
										<div className="space-y-2 text-amber-700">
											<p>Ditt medlemskap i Bokadero Arena har inte verifierats.</p>
											<ul className="list-disc pl-4">
												<li>
													Om du inte är medlem ännu,{" "}
													<Button
														variant="link"
														onClick="https://forento.link/starta-bokadero"
														newTab
													>
														starta provperiod för Bokadero Arena nu
													</Button>
													.
												</li>

												<li>Om du är medlem, verifiera ditt medlemskap nedan.</li>
											</ul>
										</div>
									</div>
								</div>

								<div className="flex-1" />

								<div className="flex gap-4">
									<SubmitButton
										variant="secondary"
										isLoading={verifyBokaderoMember.isPending}
										className="flex-1"
									>
										{verifyBokaderoMember.isPending ? "Verifierar..." : "Verifiera medlemskap"}
									</SubmitButton>
									<Button
										variant="primary"
										onClick="https://forento.link/starta-bokadero"
										newTab
										className="flex-1"
									>
										Starta provperiod
									</Button>
								</div>
							</div>
						)}
					</Group>
				)}

				<Group
					title="Change your password"
					onSubmit={async () => {
						if (currentPassword === "" || newPassword === "") {
							alert.show("Error", "Please fill in all fields.")
							return
						}
						if (newPassword !== newPasswordConfirmation) {
							alert.show("Error", "Passwords don't match.")
							return
						}
						changePassword.mutate({ current: currentPassword, new: newPassword })
					}}
				>
					<InputField
						inputType="password"
						label="Current password"
						value={currentPassword}
						onChange={setCurrentPassword}
						autoComplete="current-password"
					/>
					<InputField
						inputType="password"
						label="New password"
						value={newPassword}
						onChange={setNewPassword}
						autoComplete="new-password"
					/>
					<InputField
						inputType="password"
						label="Confirm new password"
						value={newPasswordConfirmation}
						onChange={setNewPasswordConfirmation}
						autoComplete="new-password"
					/>
					<SubmitButton variant="primary" isLoading={changePassword.isPending}>
						Change password
					</SubmitButton>
				</Group>

				{!user.user!.isCreator && (
					<Group
						horizontal
						title="Delete account"
						onSubmit={async () => {
							if (deleteAccountPassword.length === 0) return

							const dialog = await alert.confirm(
								"Delete account",
								"Are you sure you want to delete your account? This action is irreversible.",
							)
							if (!dialog.result) return

							await deleteAccount.mutateAsync({ password: deleteAccountPassword })
							dialog.close()
						}}
					>
						<InputField
							inputType="password"
							label="Current password"
							value={deleteAccountPassword}
							onChange={setDeleteAccountPassword}
							autoComplete="current-password"
						/>
						<SubmitButton
							variant="danger"
							isDisabled={deleteAccountPassword.length === 0}
							isLoading={deleteAccount.isPending}
						>
							Delete account
						</SubmitButton>
					</Group>
				)}
			</Groups>
		</Layout>
	)
}

const Groups = styled.div`
	display: flex;
	flex-direction: column;
	gap: 24px;

	@media (min-width: 1000px) {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 42px;
		padding: 0;
	}
`

const Row = styled.div`
	display: flex;
	align-items: center;
	gap: 24px;

	@media (min-width: 1000px) {
		flex-wrap: wrap;
	}
`

export default SettingsPage
